import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LuSearch,
  LuX,
  LuServer,
  LuDatabase,
  LuHardDrive,
  LuNetwork,
  LuActivity,
  LuLoader,
  LuChevronRight,
  LuCommand,
} from 'react-icons/lu';
import { api } from '../utils/api';

// ─── helpers ─────────────────────────────────────────────────────────────────

const TYPE_ICON = {
  vm: LuServer,
  database: LuDatabase,
  storage: LuHardDrive,
  network: LuNetwork,
  load_balancer: LuActivity,
};

const STATUS_STYLES = {
  running:    'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  stopped:    'bg-zinc-700/50 text-zinc-400 border-zinc-600/30',
  pending:    'bg-amber-500/15 text-amber-400 border-amber-500/30',
  error:      'bg-red-500/15 text-red-400 border-red-500/30',
  failed:     'bg-red-500/15 text-red-400 border-red-500/30',
  not_found:  'bg-zinc-700/50 text-zinc-500 border-zinc-600/30',
  terminated: 'bg-zinc-700/50 text-zinc-500 border-zinc-600/30',
};

const StatusBadge = ({ status }) => {
  const style = STATUS_STYLES[status] || 'bg-zinc-700/50 text-zinc-400 border-zinc-600/30';
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-mono uppercase tracking-wider ${style}`}
      style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.08em' }}
    >
      {status}
    </span>
  );
};

// Group resources by provider name
const groupByProvider = (resources, providers) => {
  const providerMap = {};
  providers.forEach(p => { providerMap[p.id] = p; });

  const groups = {};
  resources.forEach(r => {
    const provider = providerMap[r.provider_id];
    const key = provider ? provider.name : 'Unknown Provider';
    const pType = provider ? provider.type : 'unknown';
    if (!groups[key]) groups[key] = { providerName: key, providerType: pType, resources: [] };
    groups[key].resources.push(r);
  });
  return Object.values(groups);
};

// ─── main component ────────────────────────────────────────────────────────────

const GlobalSearch = () => {
  const [isOpen, setIsOpen]       = useState(false);
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [retrySequence, setRetrySequence] = useState(0);

  const inputRef     = useRef(null);
  const triggerRef   = useRef(null);
  const dialogRef    = useRef(null);
  const requestSequence = useRef(0);
  const debounceRef  = useRef(null);
  const navigate     = useNavigate();

  // Flatten grouped results into a single array for keyboard nav
  const flatResults = results.flatMap(g => g.resources);

  // ── open/close ───────────────────────────────────────────────────────────────
  const openSearch = useCallback(() => {
    setIsOpen(true);
    setQuery('');
    setResults([]);
    setSearchError(null);
    setSelectedIdx(0);
  }, []);

  const closeSearch = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setResults([]);
    setSearchError(null);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  // ── keyboard shortcut: CMD+K / CTRL+K ─────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) closeSearch();
        else openSearch();
      }
      if (e.key === 'Escape' && isOpen) closeSearch();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, openSearch, closeSearch]);

  // ── focus input when modal opens ────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // ── load providers once (for display names) ─────────────────────────────────
  useEffect(() => {
    api.getProviders()
      .then(res => setProviders(res.data || []))
      .catch(() => {});
  }, []);

  // ── search with debounce ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim()) {
      requestSequence.current += 1;
      setResults([]);
      setSearchError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setSearchError(null);
    const sequence = ++requestSequence.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.searchResources({ q: query.trim() });
        if (sequence !== requestSequence.current) return;
        const grouped = groupByProvider(res.data || [], providers);
        setResults(grouped);
        setSelectedIdx(0);
      } catch (e) {
        if (sequence !== requestSequence.current || e?.code === 'ERR_CANCELED') return;
        setResults([]);
        setSearchError('Search is unavailable. Your inventory has not been treated as empty.');
      } finally {
        if (sequence === requestSequence.current) setLoading(false);
      }
    }, 200);

    return () => clearTimeout(debounceRef.current);
  }, [query, isOpen, providers, retrySequence]);

  // ── keyboard navigation ─────────────────────────────────────────────────────
  const navigateTo = useCallback((resource) => {
    navigate(`/resources/${resource.id}`);
    closeSearch();
  }, [navigate, closeSearch]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx(i => Math.min(i + 1, flatResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && flatResults[selectedIdx]) {
        navigateTo(flatResults[selectedIdx]);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, flatResults, selectedIdx, navigateTo]);

  // ── empty / no-query state ──────────────────────────────────────────────────
  const showEmpty = isOpen && query.trim() && !loading && !searchError && flatResults.length === 0;
  const showHint  = isOpen && !query.trim();

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Trigger button in Navbar ─────────────────────────────────────── */}
      <button
        ref={triggerRef}
        data-testid="global-search-trigger"
        onClick={openSearch}
        aria-label="Search resources"
        className="
          flex items-center gap-2 p-2 xl:px-3 xl:py-1.5 rounded-lg
          bg-zinc-800/70 border border-zinc-700/50
          text-zinc-500 hover:text-zinc-300 hover:border-zinc-600
          transition-all duration-150 text-sm
        "
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      >
        <LuSearch className="w-3.5 h-3.5" />
        <span className="hidden xl:inline text-xs text-zinc-500">Search resources…</span>
        <span className="hidden xl:flex ml-3 items-center gap-0.5 text-[10px] text-zinc-600 border border-zinc-700 rounded px-1 py-0.5">
          <LuCommand className="w-2.5 h-2.5" />K
        </span>
      </button>

      {/* ── Modal overlay ────────────────────────────────────────────────── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="search-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[9999] flex items-start justify-center pt-[10vh]"
            style={{ backdropFilter: 'blur(8px)', background: 'rgba(0,0,0,0.65)' }}
            onClick={(e) => { if (e.target === e.currentTarget) closeSearch(); }}
          >
            <motion.div
              ref={dialogRef}
              key="search-panel"
              initial={{ opacity: 0, y: -16, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.97 }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              data-testid="global-search-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Search resources"
              className="
                w-full max-w-2xl mx-4 rounded-xl overflow-hidden
                border border-zinc-700/60
                shadow-2xl shadow-black/60
              "
              style={{ background: '#0e0e0e' }}
              onKeyDown={(event) => {
                if (event.key !== 'Tab') return;
                const focusable = [...dialogRef.current.querySelectorAll('button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')];
                if (!focusable.length) return;
                const first = focusable[0]; const last = focusable[focusable.length - 1];
                if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
                else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
              }}
            >
              {/* Input bar */}
              <div className="flex items-center gap-3 px-4 py-3.5 border-b border-zinc-800">
                {loading
                  ? <LuLoader className="w-4 h-4 text-zinc-400 animate-spin flex-shrink-0" />
                  : <LuSearch className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                }
                <input
                  ref={inputRef}
                  data-testid="global-search-input"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search resources by name, type, region, tags…"
                  className="
                    flex-1 bg-transparent text-zinc-100 placeholder-zinc-600
                    text-sm outline-none
                  "
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  autoComplete="off"
                  spellCheck={false}
                  aria-label="Search resources"
                  role="combobox"
                  aria-expanded={flatResults.length > 0}
                  aria-controls="global-search-results"
                  aria-activedescendant={flatResults[selectedIdx] ? `global-search-option-${flatResults[selectedIdx].id}` : undefined}
                />
                {query && (
                  <button
                    onClick={() => { setQuery(''); setResults([]); }}
                    aria-label="Clear search"
                    className="p-1 rounded text-zinc-600 hover:text-zinc-300 transition-colors"
                  >
                    <LuX className="w-3.5 h-3.5" />
                  </button>
                )}
                <kbd
                  className="hidden sm:flex text-[10px] text-zinc-600 border border-zinc-700 rounded px-1.5 py-0.5"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  ESC
                </kbd>
              </div>

              {/* Results */}
              <div id="global-search-results" role="listbox" aria-label="Resource search results" className="max-h-[60vh] overflow-y-auto">
                {showHint && (
                  <div className="px-5 py-10 text-center">
                    <LuSearch className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 text-sm" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      Type to search across all resources
                    </p>
                    <p className="text-zinc-700 text-xs mt-1">
                      Match by name · type · region · tags
                    </p>
                  </div>
                )}

                {showEmpty && (
                  <div className="px-5 py-10 text-center">
                    <p className="text-zinc-500 text-sm" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      No resources found for&nbsp;
                      <span className="text-zinc-300">"{query}"</span>
                    </p>
                  </div>
                )}
                {searchError && <div className="px-5 py-8 text-center" role="alert"><p className="text-sm text-red-300">{searchError}</p><button onClick={() => setRetrySequence((value) => value + 1)} className="mt-3 min-h-10 rounded-lg border border-red-400/30 px-3 text-sm text-red-100">Retry search</button></div>}

                {results.map((group) => {
                  return (
                    <div key={group.providerName} className="py-2">
                      {/* Provider group header */}
                      <div className="px-4 pb-1 pt-2 flex items-center gap-2">
                        <span
                          className="text-[10px] uppercase tracking-widest text-zinc-600 font-medium"
                          style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.12em' }}
                        >
                          {group.providerName}
                        </span>
                        <span className="text-[10px] text-zinc-700 border border-zinc-800 rounded px-1">
                          {group.providerType}
                        </span>
                        <span className="text-[10px] text-zinc-700 ml-auto">
                          {group.resources.length} result{group.resources.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* Resource rows */}
                      {group.resources.map((resource) => {
                        const globalIdx = flatResults.indexOf(resource);
                        const isSelected = globalIdx === selectedIdx;
                        const Icon = TYPE_ICON[resource.type] || LuServer;

                        return (
                          <button
                            id={`global-search-option-${resource.id}`}
                            role="option"
                            aria-selected={isSelected}
                            key={resource.id}
                            data-testid={`search-result-${resource.id}`}
                            onClick={() => navigateTo(resource)}
                            onMouseEnter={() => setSelectedIdx(globalIdx)}
                            className={`
                              w-full text-left px-4 py-2.5
                              flex items-center gap-3
                              transition-colors duration-75
                              ${isSelected ? 'bg-zinc-800/70' : 'hover:bg-zinc-800/40'}
                            `}
                          >
                            {/* Type icon */}
                            <div className="w-7 h-7 rounded-md bg-zinc-800 border border-zinc-700/50 flex items-center justify-center flex-shrink-0">
                              <Icon className="w-3.5 h-3.5 text-zinc-400" />
                            </div>

                            {/* Name + meta */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span
                                  className="text-sm text-zinc-100 truncate"
                                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                                >
                                  {resource.name}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span
                                  className="text-[11px] text-zinc-600"
                                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                                >
                                  {resource.type}
                                </span>
                                <span className="text-zinc-700">·</span>
                                <span
                                  className="text-[11px] text-zinc-600"
                                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                                >
                                  {resource.region}
                                </span>
                                {resource.tags?.length > 0 && (
                                  <>
                                    <span className="text-zinc-700">·</span>
                                    <span
                                      className="text-[11px] text-zinc-700 truncate max-w-[120px]"
                                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                                    >
                                      {resource.tags.slice(0, 3).join(', ')}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Status badge */}
                            <StatusBadge status={resource.status} />

                            {/* Arrow */}
                            <LuChevronRight
                              className={`w-3.5 h-3.5 flex-shrink-0 transition-colors ${
                                isSelected ? 'text-zinc-400' : 'text-zinc-700'
                              }`}
                            />
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              {flatResults.length > 0 && (
                <div className="px-4 py-2.5 border-t border-zinc-800 flex items-center gap-4">
                  <span className="text-[10px] text-zinc-700" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {flatResults.length} result{flatResults.length !== 1 ? 's' : ''}
                  </span>
                  <div className="flex items-center gap-3 ml-auto">
                    <span className="flex items-center gap-1 text-[10px] text-zinc-700" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      <kbd className="border border-zinc-700 rounded px-1 text-[9px]">↑↓</kbd> navigate
                    </span>
                    <span className="flex items-center gap-1 text-[10px] text-zinc-700" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      <kbd className="border border-zinc-700 rounded px-1 text-[9px]">↵</kbd> open
                    </span>
                    <span className="flex items-center gap-1 text-[10px] text-zinc-700" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      <kbd className="border border-zinc-700 rounded px-1 text-[9px]">ESC</kbd> close
                    </span>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default GlobalSearch;
